
import Vue from 'vue'
import { apiService } from '../service/api.service'

interface Model {
  id: number
  name: string
  parameters: Record<string, any>
  refreshing?: boolean
}

interface Gpu {
  id: number
  name: string
  vramSizeGb: number
}

interface Quantization {
  id: number
  name: string
  bits: number
}

export default Vue.extend({
  name: 'VRamCalculator',

  data () {
    return {
      activeTab: 0,

      // Models
      models: [] as Model[],
      modelsLoading: false,
      modelSearch: '',
      modelHeaders: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'Model Name', value: 'name' },
        { text: 'Parameters', value: 'parameters', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' }
      ],

      // GPUs
      gpus: [] as Gpu[],
      gpusLoading: false,
      gpuSearch: '',
      gpuHeaders: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'GPU Name', value: 'name' },
        { text: 'VRAM Size', value: 'vramSizeGb', width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' }
      ],

      // Add Model Dialog
      addModelDialog: false,
      addingModel: false,
      newModel: {
        name: ''
      },

      // Model Parameters Dialog
      paramsDialog: false,
      selectedModel: null as Model | null,

      // GPU Dialog
      gpuDialog: false,
      editingGpu: false,
      savingGpu: false,
      gpuForm: {
        id: null as number | null,
        name: '',
        vramSizeGb: null as number | null
      },

      // Delete Dialog
      deleteDialog: false,
      deleteType: '' as 'model' | 'gpu' | 'quantization',
      deleteItem: null as Model | Gpu | Quantization | null,
      deleting: false,

      // Sync from Model Tags
      syncing: false,

      // Quantizations
      quantizations: [] as Quantization[],
      quantizationsLoading: false,
      quantizationSearch: '',
      quantizationHeaders: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: 'Name', value: 'name' },
        { text: 'Bits', value: 'bits', width: '100px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' }
      ],

      // Quantization Dialog
      quantizationDialog: false,
      editingQuantization: false,
      savingQuantization: false,
      quantizationForm: {
        id: null as number | null,
        name: '',
        bits: null as number | null
      },

      // Seed Quantizations
      seedingQuantizations: false,

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },

  mounted () {
    this.fetchModels()
    this.fetchGpus()
    this.fetchQuantizations()
  },

  methods: {
    // Notification helper
    showNotification (message: string, color = 'success') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },

    // ==================== Models Methods ====================
    async fetchModels () {
      this.modelsLoading = true
      try {
        const response = await apiService.get('admin/llm_models/vram-calculator/models')
        if (response.data.success) {
          this.models = response.data.data.map((m: Model) => ({ ...m, refreshing: false }))
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to fetch models', 'error')
      } finally {
        this.modelsLoading = false
      }
    },

    async syncFromModelTags () {
      this.syncing = true
      try {
        const response = await apiService.post('admin/llm_models/vram-calculator/sync-from-model-tags')
        if (response.data.success) {
          const { added, skipped, failed } = response.data.data
          this.showNotification(`Sync completed. Added: ${added}, Skipped: ${skipped}, Failed: ${failed}`)
          this.fetchModels()
        } else {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to sync from model tags', 'error')
      } finally {
        this.syncing = false
      }
    },

    openAddModelDialog () {
      this.newModel.name = ''
      this.addModelDialog = true
    },

    async addModel () {
      if (!this.newModel.name.trim()) {
        this.showNotification('Model name is required', 'error')
        return
      }

      this.addingModel = true
      try {
        const response = await apiService.post('admin/llm_models/vram-calculator/models', {
          model_name: this.newModel.name.trim()
        })
        if (response.data.success) {
          this.showNotification(response.data.message)
          this.addModelDialog = false
          this.fetchModels()
        } else {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to add model', 'error')
      } finally {
        this.addingModel = false
      }
    },

    showModelParams (model: Model) {
      this.selectedModel = model
      this.paramsDialog = true
    },

    async refreshModel (model: Model) {
      model.refreshing = true
      try {
        const response = await apiService.put(`admin/llm_models/vram-calculator/models/${encodeURIComponent(model.name)}`)
        if (response.data.success) {
          this.showNotification('Model updated successfully')
          this.fetchModels()
        } else {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to refresh model', 'error')
      } finally {
        model.refreshing = false
      }
    },

    confirmDeleteModel (model: Model) {
      this.deleteType = 'model'
      this.deleteItem = model
      this.deleteDialog = true
    },

    // ==================== GPUs Methods ====================
    async fetchGpus () {
      this.gpusLoading = true
      try {
        const response = await apiService.get('admin/llm_models/vram-calculator/gpus')
        if (response.data.success) {
          this.gpus = response.data.data
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to fetch GPUs', 'error')
      } finally {
        this.gpusLoading = false
      }
    },

    openAddGpuDialog () {
      this.editingGpu = false
      this.gpuForm = {
        id: null,
        name: '',
        vramSizeGb: null
      }
      this.gpuDialog = true
    },

    openEditGpuDialog (gpu: Gpu) {
      this.editingGpu = true
      this.gpuForm = {
        id: gpu.id,
        name: gpu.name,
        vramSizeGb: gpu.vramSizeGb
      }
      this.gpuDialog = true
    },

    closeGpuDialog () {
      this.gpuDialog = false
      this.gpuForm = {
        id: null,
        name: '',
        vramSizeGb: null
      }
    },

    async saveGpu () {
      if (!this.gpuForm.name.trim()) {
        this.showNotification('GPU name is required', 'error')
        return
      }
      if (this.gpuForm.vramSizeGb === null || this.gpuForm.vramSizeGb <= 0) {
        this.showNotification('Valid VRAM size is required', 'error')
        return
      }

      this.savingGpu = true
      try {
        let response
        if (this.editingGpu && this.gpuForm.id) {
          response = await apiService.put(`admin/llm_models/vram-calculator/gpus/${this.gpuForm.id}`, {
            name: this.gpuForm.name.trim(),
            vram_size_gb: this.gpuForm.vramSizeGb
          })
        } else {
          response = await apiService.post('admin/llm_models/vram-calculator/gpus', {
            name: this.gpuForm.name.trim(),
            vram_size_gb: this.gpuForm.vramSizeGb
          })
        }

        if (response.data.success) {
          this.showNotification(response.data.message)
          this.closeGpuDialog()
          this.fetchGpus()
        } else {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to save GPU', 'error')
      } finally {
        this.savingGpu = false
      }
    },

    confirmDeleteGpu (gpu: Gpu) {
      this.deleteType = 'gpu'
      this.deleteItem = gpu
      this.deleteDialog = true
    },

    // ==================== Delete Methods ====================
    async executeDelete () {
      if (!this.deleteItem) return

      this.deleting = true
      try {
        let response
        if (this.deleteType === 'model') {
          response = await apiService.delete(`admin/llm_models/vram-calculator/models/${encodeURIComponent((this.deleteItem as Model).name)}`)
        } else if (this.deleteType === 'gpu') {
          response = await apiService.delete(`admin/llm_models/vram-calculator/gpus/${(this.deleteItem as Gpu).id}`)
        } else if (this.deleteType === 'quantization') {
          response = await apiService.delete(`admin/llm_models/vram-calculator/quantizations/${(this.deleteItem as Quantization).id}`)
        }

        if (response && response.data.success) {
          this.showNotification(response.data.message)
          this.deleteDialog = false
          if (this.deleteType === 'model') {
            this.fetchModels()
          } else if (this.deleteType === 'gpu') {
            this.fetchGpus()
          } else if (this.deleteType === 'quantization') {
            this.fetchQuantizations()
          }
        } else if (response) {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to delete', 'error')
      } finally {
        this.deleting = false
        this.deleteItem = null
      }
    },

    // ==================== Quantization Methods ====================
    async fetchQuantizations () {
      this.quantizationsLoading = true
      try {
        const response = await apiService.get('admin/llm_models/vram-calculator/quantizations')
        if (response.data.success) {
          this.quantizations = response.data.data
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to fetch quantizations', 'error')
      } finally {
        this.quantizationsLoading = false
      }
    },

    openAddQuantizationDialog () {
      this.editingQuantization = false
      this.quantizationForm = {
        id: null,
        name: '',
        bits: null
      }
      this.quantizationDialog = true
    },

    openEditQuantizationDialog (quantization: Quantization) {
      this.editingQuantization = true
      this.quantizationForm = {
        id: quantization.id,
        name: quantization.name,
        bits: quantization.bits
      }
      this.quantizationDialog = true
    },

    closeQuantizationDialog () {
      this.quantizationDialog = false
      this.quantizationForm = {
        id: null,
        name: '',
        bits: null
      }
    },

    async saveQuantization () {
      if (!this.quantizationForm.name.trim()) {
        this.showNotification('Quantization name is required', 'error')
        return
      }
      if (this.quantizationForm.bits === null || this.quantizationForm.bits <= 0) {
        this.showNotification('Valid bits value is required', 'error')
        return
      }

      this.savingQuantization = true
      try {
        let response
        if (this.editingQuantization && this.quantizationForm.id) {
          response = await apiService.put(`admin/llm_models/vram-calculator/quantizations/${this.quantizationForm.id}`, {
            name: this.quantizationForm.name.trim(),
            bits: this.quantizationForm.bits
          })
        } else {
          response = await apiService.post('admin/llm_models/vram-calculator/quantizations', {
            name: this.quantizationForm.name.trim(),
            bits: this.quantizationForm.bits
          })
        }

        if (response.data.success) {
          this.showNotification(response.data.message)
          this.closeQuantizationDialog()
          this.fetchQuantizations()
        } else {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to save quantization', 'error')
      } finally {
        this.savingQuantization = false
      }
    },

    confirmDeleteQuantization (quantization: Quantization) {
      this.deleteType = 'quantization'
      this.deleteItem = quantization
      this.deleteDialog = true
    },

    async seedDefaultQuantizations () {
      this.seedingQuantizations = true
      try {
        const response = await apiService.post('admin/llm_models/vram-calculator/quantizations/seed-defaults')
        if (response.data.success) {
          const { added, skipped } = response.data.data
          this.showNotification(`Seeded ${added} quantizations, skipped ${skipped} existing.`)
          this.fetchQuantizations()
        } else {
          this.showNotification(response.data.error, 'error')
        }
      } catch (error) {
        const err = error as { response?: { data?: { error?: string } } }
        this.showNotification(err.response?.data?.error || 'Failed to seed default quantizations', 'error')
      } finally {
        this.seedingQuantizations = false
      }
    },

    // ==================== Helper Methods ====================
    formatParamName (key: string): string {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase())
    },

    formatParamValue (key: string, value: unknown): string {
      if (key === 'num_params' && typeof value === 'number') {
        if (value >= 1e9) {
          return `${(value / 1e9).toFixed(2)}B`
        } else if (value >= 1e6) {
          return `${(value / 1e6).toFixed(2)}M`
        }
      }
      return String(value)
    }
  }
})
